import React, { useRef } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useClickAway } from 'react-use';
import styled, { css } from 'styled-components';

import { IconClose } from '@/icons';

import { Button } from '../Button/Button';
import { Flex } from '../Flex/Flex';
import { T } from '../Text/Text';

const StyledWrapper = styled.div<{
  readonly transition: TransitionStatus;
  readonly width: string | undefined;
  readonly scrollable: boolean;
}>`
  background-color: ${({ theme }) => theme.color('#FFFFFF')};
  box-shadow: 0px 22px 7px 3px rgba(35, 30, 30, 0.1);
  color: ${({ theme }) => theme.color('#1B1A1F')};
  height: 100%;
  overflow: hidden;
  position: fixed;
  right: 0;
  top: 0;
  ${({ width }) => css`
    width: ${width || '490px'};
  `};
  z-index: ${({ theme }) => theme.zindex.nav};
  padding: 24px;
  gap: 24px;

  ${({ scrollable }) => css`
    overflow-y: ${scrollable ? 'auto' : 'hidden'};
    overflow-x: hidden;
  `}

  display: flex;
  flex-direction: column;

  transition: transform 500ms;
  transform: translate3d(100%, 0, 0);
  ${({ transition }) =>
    (transition === 'entered' || transition === 'entering') &&
    css`
      transform: translate3d(0, 0, 0);
    `}

  scrollbar-width: thin;
  scrollbar-color: #ccc #f0f0f0;

  &::-webkit-scrollbar {
    width: 12px;
    height: 12px;
    background-color: #f0f0f0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 12px;
    border: 1px solid #f0f0f0;
  }

  /* For Firefox */
  &::-moz-scrollbar {
    width: 12px;
    height: 12px;
  }

  &::-moz-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 12px;
    border: 1px solid #f0f0f0;
  }
`;

const StyledButton = styled(Button)`
  align-self: flex-start;
  margin-top: 5px;
`;

const StyledTitlesWrapper = styled(Flex)`
  overflow: hidden;
`;

const StyledTextOverflowEllipsis = styled(T)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type Props = {
  readonly isOpen: boolean;
  readonly onClose?: () => void;
  readonly title: React.ReactNode;
  readonly children: React.ReactNode;
  readonly testId?: string;
  readonly subtitle?: React.ReactNode;
  readonly width?: string;
  readonly scrollable?: boolean;
};

export const Sidebar = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  testId,
  width,
  scrollable = false,
}: Props) => {
  const childRef = useRef<HTMLDivElement>(null);
  useClickAway(childRef, () => onClose?.());
  return (
    <Transition
      in={isOpen}
      mountOnEnter
      timeout={500}
      unmountOnExit
      nodeRef={childRef}
    >
      {(state) => (
        <StyledWrapper
          ref={childRef}
          transition={state}
          scrollable={scrollable}
          width={width}
          data-testid={testId}
        >
          <Flex align="center" justify="space-between">
            <StyledTitlesWrapper direction="column" gap="12px 0px">
              <StyledTextOverflowEllipsis h2>
                {title}
              </StyledTextOverflowEllipsis>

              {subtitle ? (
                <StyledTextOverflowEllipsis h3>
                  {subtitle}
                </StyledTextOverflowEllipsis>
              ) : null}
            </StyledTitlesWrapper>
            <StyledButton mode="transparent" onClick={onClose} size="small">
              <IconClose />
            </StyledButton>
          </Flex>

          <Flex direction="column" gap={32} grow>
            {children}
          </Flex>
        </StyledWrapper>
      )}
    </Transition>
  );
};
